import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  FileText,
  Image as ImageIcon,
  Code,
  FileJson,
  Cloud,
  Upload,
  X,
  AlertCircle
} from 'lucide-react';

/**
 * Multi-format input component - Professional interface for threat modeling input
 * Supports: Text, GitHub repos, Images, JSON/YAML, Terraform, CloudFormation, Kubernetes, etc.
 */
const MultiFormatInput = ({ onSubmit, loading }) => {
  const [inputMode, setInputMode] = useState('text');
  const [textInput, setTextInput] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);

  // Accepted file types
  const acceptedTypes = {
    text: {
      'text/plain': ['.txt', '.md'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    image: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/svg+xml': ['.svg'],
      'image/webp': ['.webp']
    },
    structured: {
      'application/json': ['.json'],
      'text/yaml': ['.yaml', '.yml'],
      'text/xml': ['.xml'],
      'text/x-terraform': ['.tf'],
      'text/x-python': ['.py'],
      'text/plain': ['.puml', '.plantuml', '.mmd']
    },
    cloud: {
      'application/json': ['.json'],
      'text/yaml': ['.yaml', '.yml'],
      'text/x-terraform': ['.tf'],
      'text/x-cloudformation': ['.yaml', '.yml', '.json']
    }
  };

  // File drop handling
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setError(`Invalid file type. Please select supported file formats.`);
      return;
    }

    setFiles(prev => [...prev, ...acceptedFiles]);
    setError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes[inputMode] || acceptedTypes.text,
    maxFiles: 10,
    maxSize: 50 * 1024 * 1024 // 50MB
  });

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (inputMode === 'text') {
      if (!textInput.trim()) {
        setError('Please enter a system description');
        return;
      }
      onSubmit({ system_description: textInput.trim() });
    } else if (files.length > 0) {
      const formData = new FormData();
      formData.append('input_type', inputMode);
      files.forEach(file => {
        formData.append('files', file);
      });
      onSubmit(formData);
    } else {
      setError('Please select files to upload or provide input');
    }
  };

  // Example templates
  const exampleTemplates = {
    web_app: `E-commerce Web Application

Architecture:
- Frontend: React.js SPA served via CloudFront CDN
- Backend: Node.js REST API on AWS Lambda
- Database: PostgreSQL RDS Multi-AZ with read replicas
- Cache: Redis ElastiCache cluster
- Search: Elasticsearch for product search
- Queue: SQS for order processing
- Storage: S3 for product images and user uploads

Authentication:
- JWT tokens with 24h expiration
- OAuth2 integration (Google, Facebook)
- MFA for admin accounts
- Password policy enforcement

Data Handling:
- Stores user PII (name, email, address, payment info)
- Processes credit card data (via Stripe)
- Session data in Redis
- Audit logs in CloudWatch`,

    ai_system: `AI Customer Support System

Architecture:
- Foundation Model: GPT-4 via OpenAI API
- Vector Database: Pinecone for embeddings
- Orchestration: LangChain
- Backend: Python FastAPI
- Queue: RabbitMQ for async processing
- Storage: MongoDB for conversation history

Agent Capabilities:
- Multi-turn conversations
- Knowledge base retrieval (RAG)
- Function calling for ticket creation
- Sentiment analysis
- Language translation

Security:
- Prompt injection filtering
- Content moderation
- Rate limiting per user
- API key rotation
- Audit logging

Data:
- Customer conversations (PII)
- Ticket history
- Knowledge base articles
- User preferences`,

    cloud_infra: `Cloud Infrastructure - Microservices

Kubernetes Cluster on AWS EKS:

Services:
- API Gateway (Kong)
- User Service (Node.js)
- Product Service (Python)
- Order Service (Java Spring Boot)
- Payment Service (Go)
- Notification Service (Node.js)

Data Stores:
- PostgreSQL (user, products, orders)
- MongoDB (analytics)
- Redis (session, cache)
- S3 (file storage)

Infrastructure:
- EKS cluster (3 AZs)
- Application Load Balancer
- NAT Gateway
- VPC with public/private subnets
- RDS Multi-AZ
- ElastiCache cluster

Security:
- IAM roles for pods
- Network policies
- Service mesh (Istio)
- Secrets in AWS Secrets Manager
- KMS encryption`
  };

  const inputModes = [
    {
      id: 'text',
      name: 'Text Description',
      icon: FileText,
      description: 'Enter system description as text'
    },
    {
      id: 'image',
      name: 'Architecture Diagrams',
      icon: ImageIcon,
      description: 'Upload architecture diagrams and screenshots'
    },
    {
      id: 'structured',
      name: 'Structured Documents',
      icon: FileJson,
      description: 'JSON, YAML, XML configuration files'
    },
    {
      id: 'cloud',
      name: 'Infrastructure as Code',
      icon: Cloud,
      description: 'Terraform, CloudFormation, Kubernetes'
    }
  ];

  return (
    <div className="multi-format-input">
      {/* Input Mode Selector */}
      <div className="input-mode-selector">
        <h3 className="mode-selector-title">Select Input Method</h3>
        <div className="mode-grid">
          {inputModes.map(mode => {
            const IconComponent = mode.icon;
            return (
              <button
                key={mode.id}
                className={`mode-card ${inputMode === mode.id ? 'active' : ''} ${mode.featured ? 'featured' : ''}`}
                onClick={() => setInputMode(mode.id)}
                title={mode.description}
              >
                <div className="mode-card-icon">
                  <IconComponent size={24} />
                </div>
                <div className="mode-card-content">
                  <h4 className="mode-card-title">{mode.name}</h4>
                  <p className="mode-card-description">{mode.description}</p>
                  {mode.featured && <span className="mode-badge">Recommended</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="input-form">
        {/* Text Input */}
        {inputMode === 'text' && (
          <div className="input-section">
            <div className="input-header">
              <h3>
                <FileText size={24} className="inline-icon" />
                System Description
              </h3>
              <p>Describe your system architecture, components, and security considerations</p>
            </div>

            <div className="textarea-container">
              <textarea
                className="system-description-textarea"
                placeholder="Enter your system description here..."
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                rows={12}
              />

              {/* Quick Templates */}
              <div className="template-buttons">
                <span className="template-label">Quick Templates:</span>
                {Object.entries(exampleTemplates).map(([key, template]) => (
                  <button
                    key={key}
                    type="button"
                    className="template-btn"
                    onClick={() => setTextInput(template)}
                  >
                    {key.replace('_', ' ').toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* File Upload */}
        {(inputMode === 'image' || inputMode === 'structured' || inputMode === 'cloud') && (
          <div className="input-section">
            <div className="input-header">
              <h3>
                {inputMode === 'image' && <ImageIcon size={24} className="inline-icon" />}
                {inputMode === 'structured' && <FileJson size={24} className="inline-icon" />}
                {inputMode === 'cloud' && <Cloud size={24} className="inline-icon" />}
                {inputMode === 'image' && 'Architecture Diagrams'}
                {inputMode === 'structured' && 'Structured Documents'}
                {inputMode === 'cloud' && 'Infrastructure as Code'}
              </h3>
              <p>
                {inputMode === 'image' && 'Upload diagrams, flowcharts, or screenshots of your system architecture'}
                {inputMode === 'structured' && 'Upload JSON, YAML, XML or other structured configuration files'}
                {inputMode === 'cloud' && 'Upload Terraform, CloudFormation, Kubernetes manifests or other IaC files'}
              </p>
            </div>

            {/* Dropzone */}
            <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
              <input {...getInputProps()} />
              <div className="dropzone-content">
                <Upload size={48} className="dropzone-icon" />
                <h4>
                  {isDragActive ? 'Drop files here' : 'Drag and drop files here, or click to select'}
                </h4>
                <p className="dropzone-info">
                  Supports: {Object.values(acceptedTypes[inputMode] || {}).flat().join(', ')}
                </p>
                <p className="dropzone-limit">Maximum 10 files, 50MB each</p>
              </div>
            </div>

            {/* Selected Files */}
            {files.length > 0 && (
              <div className="selected-files">
                <h4>Selected Files ({files.length})</h4>
                <div className="file-list">
                  {files.map((file, index) => (
                    <div key={index} className="file-item">
                      <div className="file-info">
                        <FileText size={16} />
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={() => removeFile(index)}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Submit Button */}
        <div className="submit-section">
          <button
            type="submit"
            className="submit-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner"></div>
                Analyzing...
              </>
            ) : (
              <>
                <Upload size={20} />
                Start Threat Analysis
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MultiFormatInput;